<template>
  <div class="code-tab">
    <v-progress-linear class="loading" v-show="loading" :indeterminate="true"></v-progress-linear>
    <search-input :search="search" placeholder="Search" @on-search="handleSearch"></search-input>
    <div v-show="!loading && count !== undefined" class="total-ctn">Total: {{count}}</div>
    <list v-show="!loading" ref="box">
      <list-item v-for="code in filteredCodes" @handle-click="selectCode(code.id)" :key="code.message" :selected="detailedCode.id == code.id" slot="item">
        <code-item :code="code" @edit="openEditCodeDialog(code)"></code-item>
      </list-item>
      <info-card v-show="detailedCode.id" slot="info-card" :style="{'margin-top': detailsMarginTopPx + 'px'}" ref="infoCard">
        <code-card :code="detailedCode"></code-card>
      </info-card>
    </list>
    <v-btn
      v-scroll="onScroll"
      v-show="showScrollHelper" fab
      dark fixed bottom
      right color="primary"
      @click="scrollToInfoCard">
      <v-icon>{{scrollHelperUp ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}}</v-icon>
    </v-btn>
    <edit-code-dialog
      :dialog="editCodeDialog"
      :code="editedCode"
      @edit="editCode"
      @cancel="editCodeDialog = false" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getAbsoluteElementHeight } from '@/utils/common'

import List from '@/components/List'
import ListItem from '@/components/ListItem'
import InfoCard from '@/components/InfoCard'

import CodeItem from '@/components/CodeItem'
import CodeCard from '@/components/CodeCard'
import EditCodeDialog from '@/components/EditCodeDialog'

import SearchInput from '@/components/SearchInput'

const listItemHeight = 72

export default {
  name: 'CodesTab',
  components: {
    'list': List,
    'list-item': ListItem,
    'code-item': CodeItem,
    'info-card': InfoCard,
    'code-card': CodeCard,
    'edit-code-dialog': EditCodeDialog,
    'search-input': SearchInput
  },
  data () {
    return {
      detailsMarginTopPx: 0,
      showScrollHelper: false,
      scrollHelperUp: true,

      editCodeDialog: false,
      editedCode: null
    }
  },
  computed: {
    ...mapGetters('codes', [
      'selectedCodeId',
      'filteredCodes',
      'formattedCodes',
      'search',
      'count',
      'loading',
    ]),
    ...mapGetters('transactions', [
      'transactions',
    ]),
    detailedCode() {
      let code = this.formattedCodes.find(code => code.id == this.selectedCodeId)
      if (!code) return {}
      code = JSON.parse(JSON.stringify(code))
      const unsubmittedTxForCode = this.transactions.find(
        tx => tx.code.extendedId == code.message && [1, 2].includes(tx.status))
      if (unsubmittedTxForCode) code['scm_data'] = JSON.parse(unsubmittedTxForCode.value)
      return code
    },
    detailedCodePosition() {
      return this.filteredCodes.findIndex(code => code.id == this.selectedCodeId)
    },
    detailsHeight() {
      return this.detailedCode && this.$refs.infoCard.$el.clientHeight || 774
    }
  },
  watch: {
    detailedCodePosition: {
      immediate: true,
      handler() {
        this.$nextTick(() => this.detailsMarginTopPx = this.computeDetailsMargin())
      }
    },
  },
  methods: {
    onScroll () {
      if (typeof window === 'undefined') return
      const viewportTopOffsetOfDetails = this.detailsMarginTopPx + 130
      const isViewportAboveDetails = window.pageYOffset + window.innerHeight < viewportTopOffsetOfDetails + 200
      const isViewportBelowDetails = viewportTopOffsetOfDetails < window.pageYOffset
      this.showScrollHelper = isViewportAboveDetails || isViewportBelowDetails
      this.scrollHelperUp = isViewportBelowDetails
    },
    selectCode (codeId) {
      this.$store.dispatch('codes/selectCode', { codeId })
    },
    openEditCodeDialog (code) {
      this.editedCode = code
      this.editCodeDialog = true
    },
    async editCode ({extendedId, update}) {
      await this.$store.dispatch('transactions/createTransaction', {extendedId, update})
      this.editCodeDialog = false
    },
    handleSearch (search) {
      this.$store.dispatch('codes/clearCodeSelection')
      this.$store.dispatch('codes/searchCode', search)
    },
    scrollToInfoCard () {
      window.scrollTo({
        top: this.detailsMarginTopPx + 130,
        left: 0,
        behavior: 'smooth'
      })
    },
    computeDetailsMargin () {
      // list item pointer is in first half of details card, no margin for the card
      if (this.detailedCodePosition * listItemHeight < this.detailsHeight / 2)
        return 0
      const bottomOffsetIndexes = this.count - this.detailedCodePosition
      const boxHeight = this.count * listItemHeight
      // info card has reached end of the list on the left, aligning it with the list bottom
      if (bottomOffsetIndexes <= Math.floor(this.detailsHeight / 2 / listItemHeight))
        return boxHeight - this.detailsHeight
      // moving accordingly with list item pointer being in the center of info card
      const res = this.detailedCodePosition * listItemHeight - this.detailsHeight / 2
      // align with 51px-high list items
      return res - res % listItemHeight + 200
    }
  },
  async created () {
    if (!this.search) {
      await this.$store.dispatch('codes/loadCodeList')
    }
  }
}

</script>

<style lang="scss" scoped>
.code-tab{
  position: relative;
  padding: 0px;
  background: white;
}
</style>
